import { isValidPhoneNumber } from "react-phone-number-input";
import { ZodEffects, ZodOptional, ZodString, z } from "zod";

import { countWordsLength } from "./generalUtils";

import { TWO } from "@/app/constants/constants";
import { ERROR_MSG } from "@/app/constants/error.constants";
import { myCountry } from "@/components/ui/PhoneInput";
import { Any } from "@/interfaces/globals";

export const imageValidator = (
  limit: number = 0,
  required: boolean = true,
  message?: string,
  allowNonImageFiles?: string[],
): z.ZodEffects<z.ZodAny, Any, Any> =>
  z.any().refine(
    (data: FileList) => {
      if (typeof data === "string") {
        return true;
      }

      let containsFileGreaterThanLimit = false;
      let containsNonImageFiles = false;
      const allowedImageTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/webp",
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/webp",
        ...(allowNonImageFiles || []),
      ];

      if (!required && (data?.length || 0) === 0) {
        return true;
      }
      if (!data) {
        return false;
      }
      if (required && (data?.length || 0) === 0) {
        return false;
      }
      Object.values(data).forEach((file) => {
        if (file.size > (limit || 2) * 1024 * 1024) {
          containsFileGreaterThanLimit = true;
        }
        if (!allowedImageTypes.includes(file.type)) {
          containsNonImageFiles = true;
        }
      });

      if (containsFileGreaterThanLimit || containsNonImageFiles) {
        return false;
      }

      return data.length === 1;
    },
    message ||
      `Max limit of 1 file. Image should be less than ${limit}MB. Only  png, jpeg, jpg, webp is supported.`,
  );

export const abnValidator = (
  required?: boolean,
  message?: string,
): z.ZodEffects<z.ZodString, Any, Any> =>
  z.string({ required_error: "ABN is required." }).refine((data: string) => {
    if (!required && !data) {
      return true;
    }
    if (required && !data) {
      return false;
    }

    const inputValue = data.replace(/\D/g, ""); // Remove non-numeric characters

    return inputValue.length === 11;
  }, message || "ABN should be 11 digits.");

export const phoneNumberValidator = (
  required?: boolean,
  message?: string,
): ZodEffects<ZodEffects<ZodString, string, string>, Any, string> =>
  z
    .string()
    .refine((data: string) => {
      // data length is less than 3 for country code
      if (!required && data.length <= 3) {
        return true;
      }
      if (required && !data) {
        return false;
      }
      // Validate phone number for the specific country

      const isValid = myCountry
        ? isValidPhoneNumber(data, myCountry)
        : isValidPhoneNumber(data);

      return isValid;
    }, message || "Please enter a valid phone number.")
    .transform((value) => (value?.length <= 3 ? null : value));

/**
 * A utility function to create a Zod schema for validating react-select values.
 * It preprocesses the input to ensure compatibility with react-select's data structure
 * and validates that the value is either an array of strings or undefined.
 * @param {string} [message] - The custom validation message for when the selection is invalid.
 * @param {boolean} [optional] - Whether the field is optional. Defaults to `false`.
 * @returns {z.ZodEffects<
 *   z.ZodOptional<z.ZodArray<z.ZodString, "many">>,
 *   string[] | undefined,
 *   unknown
 * >} A Zod schema that preprocesses and validates react-select input.
 * @example
 * const schema = z.object({
 *   manager: reactSelectValidator("Please select at least one manager."),
 * });
 * @description
 * - If the field is **optional**, it allows `undefined` or an empty array as valid values.
 * - If the field is **not optional**, it requires at least one selection and throws an error with the provided message.
 * - Preprocessing handles the following cases:
 *   - If the value is `undefined` or `null`, returns `undefined`.
 *   - If the value is a single string, wraps it in an array.
 *   - If the value is an array of objects with `label` and `value` properties (react-select format), extracts the `value` field.
 *   - If the array is empty, returns an empty array.
 */
export const reactSelectValidator = (
  message?: string,
  optional: boolean = false,
): z.ZodEffects<
  z.ZodOptional<z.ZodArray<z.ZodString, "many">>,
  string[] | undefined,
  unknown
> => {
  const arraySchema = z.array(z.string(), { required_error: message });

  const finalSchema = optional
    ? arraySchema.optional() // When optional, wrap in .optional()
    : arraySchema
        .min(1, { message: message || "Select at least one." })
        .optional(); // When not optional, still wrap in .optional()

  return z.preprocess((val: unknown) => {
    if (val === undefined || val === null) {
      return undefined;
    }

    if (Array.isArray(val)) {
      if (val.length === 0) {
        return [];
      }
      if (val.every((v) => typeof v === "string")) {
        return val;
      }
    }

    if (typeof val === "string") {
      return [val];
    }

    const value = val as unknown as
      | ({ label: string; value: string } | undefined)[]
      | undefined;

    return value?.map((v) => v?.value).filter((v) => v !== undefined) || [];
  }, finalSchema);
};

export const AddressValidatorRegex = /^[a-zA-Z0-9\s.,#-]+$/;

/*
  Address Validator
  - required: boolean (default: true)
  - message: string (default: "Please enter a valid address.")
  - minLength: number (default: 1)
  - maxLength: number (default: 50)
*/
export const addressValidator = (
  required: boolean = true,
  message?: string,
  minLength: number = 1,
  maxLength: number = 50,
): z.ZodEffects<z.ZodString, Any, Any> =>
  z
    .string()
    .trim()
    .refine(
      (data) => {
        if (!required && !data) {
          return true; // If the address is not required and no data is provided, return true (valid).
        }
        if (required && !data) {
          return false; // If the address is required and no data is provided, return false (invalid).
        }
        if (data.length < minLength) {
          return false; // If the address length is less than the minimum length, return false (invalid).
        }
        if (data.length > maxLength) {
          return false; // If the address length is greater than the maximum length, return false (invalid).
        }
        if (!AddressValidatorRegex.test(data)) {
          return false; // Ensure the address matches the regex pattern.
        }

        return true; // If all checks pass, return true (valid).
      },
      (data) => ({
        message:
          data.length > maxLength
            ? "Address is too long."
            : message || "Please enter a valid address.",
      }),
    );

/*
Basic Validator
- required: boolean (default: true)
- message: string (default: "This field is required.")
- minLength: number (default: 1)
- maxLength: number (default: 50)
*/
export const basicValidator = (
  required: boolean = true,
  message?: string,
  minLength: number = 1,
  maxLength: number = 1000,
  useReqMsg?: boolean,
):
  | z.ZodString
  | z.ZodOptional<z.ZodString>
  // eslint-disable-next-line max-params
  | ZodEffects<ZodString, string, string> => {
  let schema = z
    .string({ required_error: message })
    .trim()
    .max(maxLength, {
      message: useReqMsg
        ? message
        : `Maximum length is ${maxLength} characters`,
    });

  // Handle optional or required
  if (!required) {
    return schema.max(maxLength).optional();
  } else {
    schema = schema.min(minLength, {
      message: useReqMsg ? message : `Minimum length is ${minLength} character`,
    }); // Handle required field
  }

  return schema.refine((data) => data.replace(/\u200B/g, ""));
};

export const numericStringValidator = (
  required: boolean = true,
  message: string = "This field is required.",
  minLength: number = 1,
  maxLength: number = 1000,
): z.ZodEffects<z.ZodString, string, string> =>
  z
    .string({ required_error: message })
    .trim()
    .superRefine((data, ctx) => {
      if (!required && !data) {
        return;
      }

      if (required && !data) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message,
        });

        return;
      }

      if (data.length < minLength) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Minimum length is ${minLength} character`,
        });

        return;
      }

      if (data.length > maxLength) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Maximum length is ${maxLength} characters`,
        });

        return;
      }

      if (!/^\d+$/.test(data)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Only numeric characters are allowed.",
        });
      }
    });

const isValidDateString = (dateString: string): boolean => {
  // This regex checks for common date formats like YYYY-MM-DD, MM/DD/YYYY, etc.
  const regex = /^\d{4}-\d{2}-\d{2}|\d{2}\/\d{2}\/\d{4}$/;

  return regex.test(dateString);
};

/**
 * Date Validator
 @param {boolean} required - Whether the date is required.
 @param {string} message - The error message to display if the date is invalid.
 @returns {boolean} - The date validator.
 */
export const dateValidator = (
  required: boolean = false,
  message: string = "Invalid date format.",
): z.ZodType<Date | string | undefined, z.ZodTypeDef, Any> => {
  const unionSchema = z.union([
    z
      .string()
      .trim()
      .refine(
        (dateStr) => {
          if (dateStr === "") {
            return !required;
          }
          return isValidDateString(dateStr);
        },
        { message },
      ),
    z.date(),
  ]);

  const schema = z.preprocess((val) => {
    if (val === "" || val === null || val === undefined) {
      return undefined;
    }
    return val;
  }, unionSchema.optional());

  if (required) {
    return schema.refine((val) => val !== undefined, { message });
  }

  return schema;
};

export const wordsCountValidator = (
  required: boolean = true,
  message?: string,
  minLength: number = 1,
  maxLength: number = 1000,
):
  | z.ZodEffects<z.ZodString, string, string>
  | ZodOptional<ZodEffects<ZodString, string, string>> => {
  let schema = z.string().superRefine((data, ctx) => {
    const wordsCount = countWordsLength(data);
    if (wordsCount > maxLength) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Maximum words count is ${maxLength}`,
      });
    }
  });

  // Handle optional or required
  if (!required) {
    return schema.optional();
  } else {
    schema = z.string().superRefine((data, ctx) => {
      const wordsCount = countWordsLength(data);
      if (wordsCount < minLength) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [],
          message: message || `Minimum words count is ${minLength}`,
        });
      }
      if (wordsCount > maxLength) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [],
          message: `Maximum words count is ${maxLength}`,
        });
      }
    }); // Handle required field
  }

  return schema;
};

export const fileOrStringArrayValidator = z
  .array(
    z
      .instanceof(File, {
        message: "Required",
      })
      .or(z.string())
      .optional(),
  )
  .optional();

export interface FileUploadValidatorProps {
  required?: boolean;
  message?: string;
}

type FileArray = z.ZodArray<z.ZodUnion<[z.ZodType<File>, z.ZodString]>, "many">;

export type FileUploadValidator = FileArray | z.ZodOptional<FileArray>;

export const createFileUploadValidator = ({
  required = false,
  message = "Required",
}: FileUploadValidatorProps = {}): FileUploadValidator => {
  const baseItemValidator = z.union([
    z.instanceof(File, { message }),
    z.string(),
  ]);

  const arrayValidator = z.array(baseItemValidator);

  return required
    ? arrayValidator.min(1, "At least one file is required")
    : arrayValidator.optional();
};

// HTML String Schema Validator
export const htmlStringValidator = z
  .string()
  .max(1000, { message: `Maximum length is 1000 characters` })
  .refine(
    (html) => {
      // Remove all HTML tags except <img> and trim the resulting text
      const text = html.replace(/<(?!img)[^>]*>/g, "").trim();

      // Ensure the resulting text length is greater than 0
      return text.length > 0;
    },
    {
      message: "Description is required.",
    },
  );

interface SingleSelectValidatorProps {
  required?: boolean;
  message?: {
    required?: string;
    invalid?: string;
  };
  isUUID?: boolean;
}

/**
 * Creates a Zod schema for validating a single select input.
 * The validator enforces whether the input is required, optionally validates UUID format,
 * and provides customizable error messages.
 * @param {SingleSelectValidatorProps} props - Configuration options for the validator.
 * @param {boolean} [props.isUUID] - If true, the input must be a valid UUID string.
 * @param {object} [props.message] - Custom error messages.
 * @param {string} [props.message.invalid] - Message for invalid UUID format (default: "Invalid selection").
 * @param {string} [props.message.required] - Message when a required field is empty (default: "Required").
 * @param {boolean} [props.required] - Whether the input is mandatory.
 * @returns {ZodEffects<ZodString | ZodOptional<ZodString>, string | undefined | null, string | undefined>} - A Zod schema for validating the input.
 * @example
 * // Usage:
 * const validator = singleSelectValidator({ isUUID: true, required: true });
 * validator.parse('123e4567-e89b-12d3-a456-426614174000'); // Valid UUID
 */
export const singleSelectValidator = (
  props: SingleSelectValidatorProps,
): ZodEffects<
  ZodString | ZodOptional<ZodString>,
  string | undefined | null,
  string | undefined
> => {
  const {
    isUUID = false,
    message = {
      invalid: "Invalid selection",
      required: "Required",
    },
    required = true,
  } = props;
  const schema = z.string();

  if (!required) {
    return schema.optional().transform((data) => (!data ? null : data));
  }

  if (isUUID) {
    return schema
      .uuid({
        message: message?.invalid || "Invalid UUID format",
      })
      .refine((val) => val.trim() !== "", {
        message: message?.required || "Required",
      });
  }

  return schema.refine((val) => val.trim() !== "", {
    message: message?.required || "Required",
  });
};

interface NumberValidatorOptions {
  // Basic configuration
  required?: boolean;

  // Value constraints
  min?: number;
  max?: number;

  // Character count constraints
  minChars?: number;
  maxChars?: number;

  // Custom error messages
  messages?: {
    required?: string;
    min?: string;
    max?: string;
    minChars?: string;
    maxChars?: string;
    type?: string;
  };
}

type NumberValidatorSchema = z.ZodTypeAny;

/**
 * Creates a number validator schema with specified options.
 * @param {NumberValidatorOptions} [options] - The options for the number validator.
 * @param {boolean} [options.required] - Whether the number is required. Defaults to `true`.
 * @param {number} [options.min] - The minimum value for the number.
 * @param {number} [options.max] - The maximum value for the number.
 * @param {number} [options.minChars] - The minimum number of characters for the number. Defaults to 0.
 * @param {number} [options.maxChars] - The maximum number of characters for the number. Defaults to 10.
 * @param {object} [options.messages] - Custom error messages for validation failures.
 * @param {string} [options.messages.type] - Error message for invalid type.
 * @param {string} [options.messages.required] - Error message for required field. Defaults to "Required".
 * @param {string} [options.messages.min] - Error message for value below minimum.
 * @param {string} [options.messages.max] - Error message for value above maximum.
 * @param {string} [options.messages.minChars] - Error message for character count below minimum.
 * @param {string} [options.messages.maxChars] - Error message for character count above maximum.
 * @returns {z.ZodSchema} The number validator schema.
 */
export function numberValidator(
  options: NumberValidatorOptions = {},
): NumberValidatorSchema {
  const {
    required = true,
    min,
    max,
    minChars = 0,
    maxChars = 10,
    messages = {},
  } = options;

  // Base schema handling required/optional state
  let baseSchema = z.coerce.number({
    invalid_type_error: messages.type || "Must be a valid number",
    required_error: messages.required || "Required",
  });

  // Apply min/max constraints to the base schema
  if (min !== undefined) {
    baseSchema = baseSchema.min(min, {
      message: messages.min || `Must be at least ${min}`,
    });
  }

  if (max !== undefined) {
    baseSchema = baseSchema.max(max, {
      message: messages.max || `Must not exceed ${max}`,
    });
  }

  // Apply character count constraints
  if (minChars !== undefined || maxChars !== undefined) {
    // @ts-expect-error - refine() is not defined on ZodTypeAny
    baseSchema = baseSchema.refine(
      (val) => {
        const strLength = val.toString().trim().length;

        if (minChars !== undefined && strLength < minChars) {
          return false;
        }
        if (maxChars !== undefined && strLength > maxChars) {
          return false;
        }

        return true;
      },
      (val) => {
        const strLength = val.toString().trim().length;

        if (minChars !== undefined && strLength < minChars) {
          return {
            message:
              messages.minChars || `Must have at least ${minChars} characters`,
          };
        }

        if (maxChars !== undefined && strLength > maxChars) {
          return {
            message:
              messages.maxChars || `Must not exceed ${maxChars} characters`,
          };
        }

        return { message: "Invalid character count" };
      },
    );
  }

  // Make it optional if required is false
  const finalSchema = required ? baseSchema : baseSchema.optional();

  return finalSchema;
}

export const emailValidator = (
  required: boolean = true,
  message: string = "Invalid email address.",
):
  | ZodEffects<ZodString, string, string>
  | ZodString
  | ZodOptional<ZodEffects<ZodString, string, string>> => {
  const emailSchema = z.string({ required_error: "Email is required." }).trim();

  if (!required) {
    return emailSchema
      .refine(
        (value) =>
          value === "" ||
          value === undefined ||
          z.string().email().safeParse(value).success,
        {
          message,
        },
      )
      .optional();
  }

  return emailSchema.email({ message }).min(1);
};

interface AlphanumericValidatorProps {
  required?: boolean;
  allowSpace?: boolean;
  allowUnderscore?: boolean;
  allowHyphen?: boolean;
  maxLength?: number;
  minLength?: number;
  message?: {
    requiredMessage?: string;
    spaceMessage?: string;
    hyphenMessage?: string;
    alphanumericMessage?: string;
  };
}

interface BuildRegexProps {
  allowSpace?: boolean;
  allowHyphen?: boolean;
  allowUnderscore?: boolean;
}

const buildRegex = (options: BuildRegexProps): RegExp => {
  const base = "A-Za-z0-9";

  const charMap: Record<keyof BuildRegexProps, string> = {
    allowSpace: "\\s",
    allowHyphen: "-",
    allowUnderscore: "_",
  };

  const extras = Object.entries(options)
    .filter(([_, allowed]) => allowed)
    .map(([key]) => charMap[key as keyof BuildRegexProps])
    .join("");

  return new RegExp(`^[${base}${extras}]+$`);
};

export const alphanumericValidator = ({
  required = true,
  allowSpace = true,
  allowHyphen = false,
  allowUnderscore = false,
  maxLength = 1000,
  minLength = 0,
  message,
}: AlphanumericValidatorProps):
  | ZodEffects<ZodString, string, string>
  | ZodOptional<ZodEffects<ZodString, string, string>> => {
  const regex = buildRegex({ allowSpace, allowHyphen, allowUnderscore });
  const schema = z
    .string({
      message: message?.requiredMessage || ERROR_MSG.REQUIRED,
    })
    .trim()
    .max(maxLength, { message: `Maximum length is ${maxLength} characters` })
    .min(minLength, { message: `Minimum length is ${minLength} characters` })
    .superRefine((val, ctx) => {
      if (!required && !val) {
        return;
      }

      if (!allowSpace && val.includes(" ")) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: message?.spaceMessage || "Spaces are not allowed.",
        });
      }

      if (!allowHyphen && val.includes("-")) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: message?.hyphenMessage || "Hyphens are not allowed.",
        });
      }

      if (!regex.test(val)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            message?.alphanumericMessage ||
            "Only alphanumeric characters are allowed.",
        });
      }
    });
  if (!required) {
    return schema.optional();
  }

  return schema;
};

/**
 * Creates a Zod schema for validating a number with up to the specified number of decimal places.
 * @param {number} count - Maximum number of decimal places allowed.
 * @returns {z.ZodEffects<z.ZodNumber, number, number>} A Zod schema for validating the number.
 * @example
 * const schema = maxDecimals(4);
 * schema.parse(1);       // Valid
 * schema.parse(1.2);     // Valid
 * schema.parse(1.2345);  // Valid
 * schema.parse(1.23456); // Invalid
 */
export const maxDecimals = (
  count: number,
): z.ZodEffects<z.ZodNumber, number, number> =>
  z
    .number()
    .refine(
      (val) => new RegExp(`^\\d+(\\.\\d{1,${count}})?$`).test(val.toString()),
      { message: `Must have at most ${count} decimal places` },
    );

/**
 * Number with bounded decimal places and max digits before the decimal point.
 * Rejects scientific notation so digit limits match UI constraints.
 * @param {number} maxIntegerDigits - Maximum digits allowed left of the decimal
 * @param {number} maxDecimalPlaces - Maximum digits allowed right of the decimal
 * @returns {z.ZodEffects<z.ZodNumber, number, number>} Zod number schema with refinements
 */
export const maxIntegerDigitsAndDecimals = (
  maxIntegerDigits: number,
  maxDecimalPlaces: number,
): z.ZodType<number> =>
  maxDecimals(maxDecimalPlaces).refine(
    (val) => {
      const s = val.toString();
      if (/e/i.test(s)) {
        return false;
      }
      const [rawInt = "0"] = s.split(".");
      const intDigits = rawInt.replace(/^-/, "");

      return intDigits.length <= maxIntegerDigits;
    },
    {
      message: `Must have at most ${maxIntegerDigits} digits before the decimal`,
    },
  );

/**
 * Checks if a given string is a potential prototype pollution key.
 * @param {string} key - The string to validate.
 * @returns {boolean}  true if the key is unsafe, false otherwise.
 */
export const isPrototypePollutionKey = (
  key: string | undefined | null,
): boolean => {
  if (!key) {
    return false;
  }
  const prototypePollutionKeys = ["__proto__", "constructor", "prototype"];
  const normalized = key.trim().toLowerCase();

  return prototypePollutionKeys.includes(normalized);
};

export const fullNameSchema = z
  .string()
  .trim()
  .min(TWO, { message: ERROR_MSG.REQUIRED })
  .refine((val) => val.split(/\s+/).length >= 2, {
    message: "Please enter both first and last name.",
  });

export const urlValidator = z.string().refine(
  (val) => {
    try {
      const u = new URL(val);

      return u.protocol === "https:";
    } catch {
      return false;
    }
  },
  { message: "Please enter a web address that starts with https://" },
);

export const CustomerUserPermission = z.object({
  emailNotification: z.boolean(),
  customerPortal: z.boolean(),
  projectFinance: z.boolean(),
  workDocket: z.boolean(),
});

export type CustomerUserPermissionFormData = z.infer<
  typeof CustomerUserPermission
>;

type WithEmailAndPermissions = {
  email?: string | null;
  permissions?: Partial<CustomerUserPermissionFormData>;
};

export const requireEmailIfPermissioned = (
  data: WithEmailAndPermissions,
  ctx: z.RefinementCtx,
): void => {
  const hasAnyPermission =
    data?.permissions?.emailNotification ||
    data?.permissions?.customerPortal ||
    data?.permissions?.projectFinance ||
    data?.permissions?.workDocket;

  if (!data?.email && hasAnyPermission) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: ERROR_MSG.EMAIL_REQUIRED_IF_PERMISSION,
      path: ["email"],
    });
  }
};
